import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { API_BASE_URL } from '../config';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [apiKey, setApiKey] = useState('');
  const [account, setAccount] = useState(null);
  const navigate = useNavigate();

  // Check if user is authenticated on mount
  useEffect(() => {
    const storedApiKey = localStorage.getItem('ai_tech_api_key');
    if (storedApiKey) {
      setApiKey(storedApiKey);
      validateApiKey(storedApiKey);
    } else {
      setLoading(false);
    }
  }, []);

  // Validate API key with backend
  const validateApiKey = async (key) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/health/detailed`, {
        headers: {
          'X-API-Key': key,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setApiKey(key);
        setIsAuthenticated(true);
        setAccount({
          id: data.account_id || 0,
          name: data.account_name || `Account ${data.account_id || ''}`,
          api_key: key
        });
        setLoading(false);
      } else {
        // Invalid API key
        localStorage.removeItem('ai_tech_api_key');
        setApiKey('');
        setIsAuthenticated(false);
        setAccount(null);
        setLoading(false);
      }
    } catch (error) {
      console.error('Error validating API key:', error);
      localStorage.removeItem('ai_tech_api_key');
      setApiKey('');
      setIsAuthenticated(false);
      setAccount(null);
      setLoading(false);
    }
  };

  // Login function
  const login = async (key) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/v1/health/detailed`, {
        headers: {
          'X-API-Key': key,
        },
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('ai_tech_api_key', key);
        setApiKey(key);
        setIsAuthenticated(true);
        setAccount({
          id: data.account_id || 0,
          name: data.account_name || `Account ${data.account_id || ''}`,
          api_key: key
        });
        toast.success('Login successful!');
        navigate('/dashboard');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Invalid API key');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.message || 'Login failed');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem('ai_tech_api_key');
    setApiKey('');
    setIsAuthenticated(false);
    setAccount(null);
    navigate('/login');
    toast.success('Logged out successfully');
  };

  // Update API key
  const updateApiKey = async (newKey) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/v1/health/detailed`, {
        headers: {
          'X-API-Key': newKey,
        },
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('ai_tech_api_key', newKey);
        setApiKey(newKey);
        setIsAuthenticated(true);
        setAccount({
          id: data.account_id || 0,
          name: data.account_name || `Account ${data.account_id || ''}`,
          api_key: newKey
        });
        toast.success('API key updated successfully!');
        return true;
      } else {
        throw new Error('Invalid API key');
      }
    } catch (error) {
      console.error('Error updating API key:', error);
      toast.error('Failed to update API key');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Get authenticated headers for API calls
  const getAuthHeaders = () => {
    // Try to get API key from localStorage if state is not available
    const storedApiKey = localStorage.getItem('ai_tech_api_key');
    const keyToUse = apiKey || storedApiKey;
    
    if (!keyToUse) {
      console.error('No API key available in getAuthHeaders');
      // Instead of throwing an error, redirect to login
      logout();
      return {}; // Return empty object instead of null to prevent spread operator errors
    }
    
    return {
      'X-API-Key': keyToUse,
      'Content-Type': 'application/json',
    };
  };

  // Check if API key is valid
  const checkAuthStatus = async () => {
    if (!apiKey) {
      return false;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/health/detailed`, {
        headers: {
          'X-API-Key': apiKey,
        },
      });

      if (response.ok) {
        return true;
      } else {
        // Invalid API key, logout user
        logout();
        return false;
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      logout();
      return false;
    }
  };

  const value = {
    isAuthenticated,
    loading,
    apiKey,
    account,
    login,
    logout,
    updateApiKey,
    getAuthHeaders,
    checkAuthStatus,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
